import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import { Box, IconButton, styled, MenuItem, Popover } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { HttpService } from "../../service/HttpService";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import { useAuth } from "../../context/AuthProvider";
import dayjs from "dayjs";

const ArchiveStatus = () => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  // const [search, setSearch] = useState("");
  // const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search effect
  // useEffect(() => {
  //   const handler = setTimeout(() => {
  //     setDebouncedSearch(search);
  //   }, 500);
  //   return () => clearTimeout(handler);
  // }, [search]);

  const dateFilterData = JSON.parse(
    localStorage.getItem("archiveStatusDateFilter") || "null",
  );

  const hasValidStoredStart =
    dateFilterData?.startDate && dayjs(dateFilterData.startDate).isValid();
  const hasValidStoredEnd =
    dateFilterData?.endDate && dayjs(dateFilterData.endDate).isValid();

  const [startDate, setStartDate] = useState(
    hasValidStoredStart && hasValidStoredEnd ? dateFilterData.startDate : "",
  );
  const [endDate, setEndDate] = useState(
    hasValidStoredStart && hasValidStoredEnd ? dateFilterData.endDate : "",
  );

  // Ensure end date is never before start date
  // useEffect(() => {
  //   if (
  //     startDate &&
  //     endDate &&
  //     dayjs(endDate).isValid() &&
  //     dayjs(startDate).isValid() &&
  //     dayjs(endDate).isBefore(dayjs(startDate), "day")
  //   ) {
  //     const clearedEnd = "";
  //     setEndDate(clearedEnd);
  //     const archiveStatusDateFilter = {
  //       startDate,
  //       endDate: clearedEnd,
  //     };
  //     localStorage.setItem(
  //       "archiveStatusDateFilter",
  //       JSON.stringify(archiveStatusDateFilter),
  //     );
  //   }
  // }, [startDate]);

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const fetchHarvestStatus = async () => {
    setLoading(true);
    try {
      let endpoint = "archive-status";
      const params = [];

      const hasValidStart = !!startDate && dayjs(startDate).isValid();
      const hasValidEnd = !!endDate && dayjs(endDate).isValid();

      if (hasValidStart && hasValidEnd) {
        params.push(`start_date=${startDate}`);
        params.push(`end_date=${endDate}`);
      }

      // if (debouncedSearch) {
      //   params.push(`search=${encodeURIComponent(debouncedSearch)}`);
      // }

      if (params.length > 0) {
        endpoint += `?${params.join("&")}`;
      }

      const response = await httpService.get(endpoint);
      if (response?.data) {
        const rows = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        setData(rows);
      }
    } catch (error) {
      console.error("Error fetching harvest status:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHarvestStatus();
  }, [startDate, endDate]);

  const getWeekOfYear = (date) => {
    const d = new Date(date);
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNumber = Math.ceil(
      ((d - yearStart) / 86400000 + yearStart.getDay() + 1) / 7,
    );
    return weekNumber;
  };

  const StyledGridOverlay = styled("div")(() => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  }));

  const CustomNoRowsOverlay = () => (
    <StyledGridOverlay>
      <Box sx={{ mt: 1 }}>No Data Found</Box>
    </StyledGridOverlay>
  );

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          <IconButton onClick={(e) => handleMenuClick(e, params.row.id)}>
            <GridMoreVertIcon />
          </IconButton>
          {anchorEl && menuRowId === params.row.id && (
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                className="menu-list-items"
                onClick={() => {
                  handleMenuClose();
                  navigate("/Archive", {
                    state: {
                      roomId: params.row.room_id,
                      harvested_date: dayjs(params.row.harvested_date).format(
                        "MM-DD-YYYY",
                      ),
                      start_harvested_date:
                        dayjs(startDate).format("YYYY-MM-DD"),
                      end_harvested_date: dayjs(endDate).format("YYYY-MM-DD"),
                    },
                  });
                }}
              >
                View
              </MenuItem>
            </Popover>
          )}
        </>
      ),
    },
    {
      field: "week",
      headerName: "Week",
      flex: 1,
      minWidth: 100,
      headerClassName: "headerclass",
      valueGetter: (params) => {
        if (!params.row.harvested_date) return "";
        return getWeekOfYear(params.row.harvested_date);
      },
    },
    {
      field: "harvested_date",
      headerName: "Harvest Date",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      renderCell: (params) =>
        params.value ? dayjs(params.value).format("MM-DD-YYYY") : "-",
    },
    {
      field: "cultivars",
      headerName: "Cultivar",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      renderCell: (params) => {
        return (
          <div style={{ whiteSpace: "normal", lineHeight: "1.2" }}>
            {params.value || params.row.cultivar_name || "-"}
          </div>
        );
      },
    },
    {
      field: "room",
      headerName: "Room",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      renderCell: (params) => params.value || params.row.room_name || "-",
    },
    {
      field: "room_log_archived",
      headerName: "Room-log",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      renderCell: (params) => params.value || "-",
    },
    {
      field: "harvest_data_archived",
      headerName: "Harvested",
      flex: 1,
      minWidth: 120,
      headerClassName: "headerclass",
      renderCell: (params) => {
        const val = params.value;
        if (val === true || val === "true" || val === 1) return "Yes";
        if (val === false || val === "false" || val === 0) return "No";
        return val || "-";
      },
    },
    {
      field: "coa_archived",
      headerName: "COA",
      flex: 1,
      minWidth: 150,
      headerClassName: "headerclass",
      renderCell: (params) => params.value || "-",
    },
  ];

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="harvest">
        <div className="page-title">
          <div className="title-wrap">
            <h1>Archive Status</h1>
          </div>
        </div>

        <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-4">
          {/* <div className="header_search-wrap">
            <div className="header_search_bar common_content d-flex">
              <div className="input-group input_group">
                <button className="btn search-btn search_btn">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 21 21"
                    fill="none"
                  >
                    <circle
                      cx="9.98856"
                      cy="9.98856"
                      r="8.98856"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16.2402 16.7071L19.7643 20.222"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <input
                  type="search"
                  className="form-control form_control"
                  placeholder="Search..."
                  aria-label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div> */}

          <div className="header_search-wrap">
            <div className="header_search_bar common_content">
              <div
                className="input-group input_group"
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  backgroundColor: "unset",
                  paddingBottom: "12px",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={startDate ? dayjs(startDate) : null}
                    disableFuture
                    onChange={(newStartDate) => {
                      if (!newStartDate) {
                        setStartDate("");
                        const archiveStatusDateFilter = {
                          startDate: "",
                          endDate,
                        };
                        localStorage.setItem(
                          "archiveStatusDateFilter",
                          JSON.stringify(archiveStatusDateFilter),
                        );
                        return;
                      }

                      const formattedDate =
                        dayjs(newStartDate).format("YYYY-MM-DD");
                      setStartDate(formattedDate);
                      const archiveStatusDateFilter = {
                        startDate: formattedDate,
                        endDate,
                      };
                      localStorage.setItem(
                        "archiveStatusDateFilter",
                        JSON.stringify(archiveStatusDateFilter),
                      );
                    }}
                    format="YYYY-MM-DD"
                    sx={{ minWidth: "200px", backgroundColor: "white" }}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate ? dayjs(endDate) : null}
                    disableFuture
                    minDate={
                      startDate && dayjs(startDate).isValid()
                        ? dayjs(startDate)
                        : undefined
                    }
                    onChange={(newEndDate) => {
                      if (!newEndDate) {
                        setEndDate("");
                        const archiveStatusDateFilter = {
                          startDate,
                          endDate: "",
                        };
                        localStorage.setItem(
                          "archiveStatusDateFilter",
                          JSON.stringify(archiveStatusDateFilter),
                        );
                        return;
                      }

                      const formattedDate =
                        dayjs(newEndDate).format("YYYY-MM-DD");
                      setEndDate(formattedDate);
                      const archiveStatusDateFilter = {
                        startDate,
                        endDate: formattedDate,
                      };
                      localStorage.setItem(
                        "archiveStatusDateFilter",
                        JSON.stringify(archiveStatusDateFilter),
                      );
                    }}
                    format="YYYY-MM-DD"
                    sx={{ minWidth: "200px", backgroundColor: "white" }}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            height: `${data?.length > 0 ? "" : "200px"}`,
            width: "100%",
            marginTop: 24,
          }}
        >
          <DataGrid
            rows={data}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            sx={{
              border: "1px solid #E0E0E0",
              [`& .${gridClasses.cell}`]: { py: 1 },
              height: "calc(100vh - 350px)",
              // minHeight: 576,
            }}
            autoHeight
            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
            hideFooter
            showCellVerticalBorder
            showColumnVerticalBorder
          />
        </div>
      </div>
    </>
  );
};

export default ArchiveStatus;
